import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Plus, Layers, TrendingUp, ArrowRight, ShoppingBag, Trash2 } from "lucide-react";
import { fetchProducts, fetchBundleTiers, bundleDiscount, nextTier, money, fetchStacks, saveStack, fetchSavedStacks, deleteSavedStack } from "../lib/api";
import { useCart } from "../context/CartContext";
import { StarRating } from "../components/StarRating";
import { StackCard } from "../components/StackCard";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";

const GOAL_LABEL = {
  recovery: "Recover",
  lean: "Get Lean",
  performance: "Perform",
  essentials: "Essential",
};

export default function BundleBuilder() {
  const { addItem, setOpen } = useCart();
  const [products, setProducts] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [stacks, setStacks] = useState([]);
  const [saved, setSaved] = useState([]);
  const [selected, setSelected] = useState({}); // handle -> product

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProducts().then(setProducts);
    fetchStacks().then(setStacks).catch(() => {});
    fetchBundleTiers().then((d) => setTiers(d.tiers)).catch(() => {});
    fetchSavedStacks().then(setSaved).catch(() => {});
  }, []);

  const reloadSaved = () => fetchSavedStacks().then(setSaved).catch(() => {});

  const saveCurrent = async () => {
    if (!chosen.length) return;
    const name = window.prompt("Name your stack (e.g. My Cut Stack)", "My Stack");
    if (name === null) return;
    try {
      await saveStack(name || "My Stack", chosen.map((p) => ({ handle: p.handle, quantity: 1 })));
      toast.success("Stack saved — reorder it anytime");
      reloadSaved();
    } catch {
      toast.error("Could not save stack");
    }
  };

  const reorderSaved = (s) => {
    s.items.forEach((it) =>
      addItem({ handle: it.handle, name: it.name, dose: it.dose, price: it.price, images: [it.image] }, it.quantity)
    );
    toast.success(`${s.name} added to cart`);
    setOpen(true);
  };

  const removeSaved = async (id) => {
    await deleteSavedStack(id);
    reloadSaved();
  };

  const chosen = Object.values(selected);
  const distinct = chosen.length;
  const subtotal = useMemo(() => chosen.reduce((s, p) => s + p.price, 0), [chosen]);
  const discount = bundleDiscount(distinct, tiers);
  const savings = subtotal * (discount / 100);
  const total = subtotal - savings;
  const upcoming = nextTier(distinct, tiers);
  const maxTier = tiers[tiers.length - 1];

  const toggle = (p) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[p.handle]) delete next[p.handle];
      else next[p.handle] = p;
      return next;
    });

  const addStack = () => {
    if (!chosen.length) return;
    chosen.forEach((p) => addItem(p, 1));
    toast.success(
      `${chosen.length} peptide${chosen.length === 1 ? "" : "s"} added${
        discount > 0 ? ` — ${discount}% saving applied at checkout` : ""
      }`
    );
    setOpen(true);
  };

  return (
    <div className="pt-24 md:pt-28 pb-40">
      {/* header */}
      <div className="border-b border-[#27272A] bg-ink-800/30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent flex items-center gap-2">
            <Layers size={14} /> / Build a stack
          </span>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-4 leading-none">
            STACK &amp; SAVE
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl leading-relaxed">
            Build your own research protocol. The more you stack, the bigger the saving — applied
            automatically at checkout.
          </p>

          <div className="flex flex-wrap gap-2.5 mt-8">
            {tiers.map((t) => (
              <div
                key={t.min_items}
                className={`font-mono-tech text-xs uppercase tracking-widest px-4 py-2.5 border transition-colors ${
                  distinct >= t.min_items
                    ? "bg-accent border-accent text-white"
                    : "border-[#27272A] text-zinc-500"
                }`}
                data-testid={`tier-${t.min_items}`}
              >
                {t.min_items}+ items · {t.percent}% off
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SUGGESTED STACKS */}
      {stacks.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-14 md:pt-20">
          <Reveal className="mb-8 max-w-2xl">
            <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
              / Ready-made protocols
            </span>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-none">
              SUGGESTED STACKS
            </h2>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              Not sure where to start? Add a purpose-built protocol in one tap — the stack saving
              applies automatically at checkout.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="suggested-stacks">
            {stacks.map((s, i) => (
              <StackCard key={s.slug} stack={s} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* SAVED STACKS */}
      {saved.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-14 md:pt-20">
          <Reveal className="mb-8 max-w-2xl">
            <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
              / Your stacks
            </span>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-none">
              SAVED &amp; READY TO REORDER
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="saved-stacks">
            {saved.map((s) => {
              const stotal = s.items.reduce((a, it) => a + it.price * it.quantity, 0);
              return (
                <div key={s.id} className="bg-ink-800 border border-[#27272A] p-5 flex flex-col" data-testid={`saved-stack-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display font-extrabold text-xl tracking-tight leading-none">
                      {s.name}
                    </h3>
                    <button
                      onClick={() => removeSaved(s.id)}
                      className="text-zinc-600 hover:text-accent transition-colors"
                      data-testid={`delete-saved-${s.id}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <div className="flex -space-x-2 mt-4">
                    {s.items.map((it) => (
                      <img
                        key={it.handle}
                        src={it.image}
                        alt={it.name}
                        title={`${it.name} × ${it.quantity}`}
                        className="h-11 w-11 object-cover bg-ink-700 border border-[#27272A]"
                      />
                    ))}
                  </div>
                  <p className="font-mono-tech text-[11px] text-zinc-500 mt-3">
                    {s.items.length} item{s.items.length === 1 ? "" : "s"} · {money(stotal)}
                  </p>
                  <button
                    onClick={() => reorderSaved(s)}
                    className="mt-4 flex items-center justify-center gap-2 bg-white text-ink-900 hover:bg-accent hover:text-white font-display font-bold text-sm uppercase tracking-wide py-2.5 transition-colors"
                    data-testid={`reorder-${s.id}`}
                  >
                    <ShoppingBag size={15} /> Reorder
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* grid */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
        <Reveal className="mb-8 max-w-2xl">
          <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
            / Build your own
          </span>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-none">
            PICK YOUR PEPTIDES
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p, i) => {
            const active = !!selected[p.handle];
            return (
              <Reveal key={p.handle} delay={(i % 3) * 0.05}>
                <button
                  onClick={() => toggle(p)}
                  className={`group relative w-full text-left flex gap-4 p-4 border transition-colors ${
                    active
                      ? "border-accent bg-accent/5"
                      : "border-[#27272A] bg-ink-800 hover:border-white/40"
                  }`}
                  data-testid={`bundle-select-${p.handle}`}
                >
                  <div className="relative h-24 w-24 shrink-0 overflow-hidden bg-ink-700 clip-corner">
                    <img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-display font-extrabold text-lg leading-none tracking-tight">
                          {p.name}
                        </h3>
                        <p className="font-mono-tech text-[10px] tracking-widest uppercase text-zinc-500 mt-1">
                          {p.dose} · {GOAL_LABEL[p.goals?.[0]]}
                        </p>
                      </div>
                      <span
                        className={`h-6 w-6 shrink-0 flex items-center justify-center border transition-colors ${
                          active ? "bg-accent border-accent" : "border-[#27272A] group-hover:border-white"
                        }`}
                      >
                        {active ? <Check size={14} className="text-white" /> : <Plus size={14} className="text-zinc-500" />}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="font-display font-bold text-lg">{money(p.price)}</span>
                      {p.rating && <StarRating rating={p.rating} size={11} />}
                    </div>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>

      {/* sticky summary bar */}
      <AnimatePresence>
        {distinct > 0 && (
          <motion.div
            initial={{ y: 120 }}
            animate={{ y: 0 }}
            exit={{ y: 120 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-ink-900/85 border-t border-[#27272A]"
            data-testid="bundle-summary-bar"
          >
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex flex-col md:flex-row items-center gap-4">
              <div className="flex-1 w-full">
                <div className="flex items-center justify-between md:justify-start gap-6">
                  <span className="font-mono-tech text-[11px] uppercase tracking-widest text-zinc-400">
                    {distinct} peptide{distinct === 1 ? "" : "s"} selected
                  </span>
                  {discount > 0 && (
                    <span className="flex items-center gap-1.5 font-mono-tech text-[11px] uppercase tracking-widest text-accent">
                      <TrendingUp size={13} /> {discount}% off · save {money(savings)}
                    </span>
                  )}
                </div>
                {upcoming ? (
                  <div className="mt-2">
                    <p className="font-mono-tech text-[10px] text-zinc-500 tracking-wide mb-1.5">
                      Add {upcoming.min_items - distinct} more for {upcoming.percent}% off
                    </p>
                    <div className="h-1 bg-ink-700 max-w-md overflow-hidden">
                      <div
                        className="h-full bg-accent transition-all duration-500"
                        style={{ width: `${Math.min(100, (distinct / upcoming.min_items) * 100)}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="font-mono-tech text-[10px] text-accent tracking-wide mt-2">
                    Max saving unlocked — {maxTier?.percent}% off applied.
                  </p>
                )}
              </div>

              <div className="flex items-center gap-5 w-full md:w-auto">
                <div className="text-right">
                  {discount > 0 && (
                    <span className="font-mono-tech text-xs text-zinc-600 line-through block">
                      {money(subtotal)}
                    </span>
                  )}
                  <span className="font-display font-black text-2xl" data-testid="bundle-total">
                    {money(total)}
                  </span>
                </div>
                <button
                  onClick={saveCurrent}
                  className="hidden sm:flex items-center gap-2 border border-white/25 hover:border-white text-white font-display font-bold uppercase tracking-wide text-sm px-4 py-3.5 transition-colors"
                  data-testid="save-stack-button"
                >
                  Save
                </button>
                <button
                  onClick={addStack}
                  className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-display font-bold uppercase tracking-wide text-base px-6 py-3.5 transition-colors clip-corner"
                  data-testid="add-stack-button"
                >
                  <ShoppingBag size={17} /> Add Stack
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {distinct === 0 && (
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-zinc-400 hover:text-accent transition-colors"
          >
            Or browse the full range <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </div>
  );
}
